import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  useMediaQuery,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab
} from '@mui/material';
import {
  TouchApp,
  ShoppingCart,
  Payment,
  Restaurant,
  Sync,
  Palette,
  Analytics,
  Language,
  CheckCircle,
  ExpandMore
} from '@mui/icons-material';
import Header from './components/Header';
import Footer from './components/Footer';
import PricingSection from './components/PricingSection';
import { getKioskPricingPlans } from './components/PricingConfigs';
import PublicAiChatWidget from './components/PublicAiChatWidget';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_GRADIENTS } from '../../config/brand';

// Stil tanımlamaları (PreAccounting ile aynı)
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.primary, // BRAND'den
}));

const ContentWrapper = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
});

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.9) 0%, rgba(25, 118, 210, 0.8) 100%)',
  padding: theme.spacing(8, 0),
  minHeight: '35vh',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  color: 'white',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
}));

const StatsSection = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: '1px solid rgba(255, 255, 255, 0.2)'
}));

const ScrollProgressBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  zIndex: 1001,
  backdropFilter: 'blur(10px)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: BRAND_GRADIENTS.horizontal, // BRAND'den
    transition: 'width 0.15s ease-out',
    boxShadow: '0 0 15px rgba(13, 71, 161, 0.6)',
    borderRadius: '0 2px 2px 0',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
    animation: 'shimmer 2s infinite',
  },
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' }
  }
}));

const Kiosk: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const localizedKioskPlans = React.useMemo(
    () =>
      getKioskPricingPlans(t).map((plan, idx) =>
        idx === 0 || idx === 1
          ? { ...plan, action: 'contact' as const }
          : { ...plan, action: 'contact' as const }
      ),
    [t]
  );

  // Scroll event listener
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const features = [
    {
      icon: <TouchApp />,
      title: t('kiosk.features.touchInterface.title'),
      description: t('kiosk.features.touchInterface.description'),
      color: BRAND.colors.secondary
    },
    {
      icon: <ShoppingCart />,
      title: t('kiosk.features.orderManagement.title'),
      description: t('kiosk.features.orderManagement.description'),
      color: '#2E7D32'
    },
    {
      icon: <Payment />,
      title: t('kiosk.features.paymentIntegration.title'),
      description: t('kiosk.features.paymentIntegration.description'),
      color: '#ED6C02'
    },
    {
      icon: <Restaurant />,
      title: t('kiosk.features.menuDisplay.title'),
      description: t('kiosk.features.menuDisplay.description'),
      color: '#9C27B0'
    },
    {
      icon: <Sync />,
      title: t('kiosk.features.realTimeSync.title'),
      description: t('kiosk.features.realTimeSync.description'),
      color: '#D32F2F'
    },
    {
      icon: <Palette />,
      title: t('kiosk.features.customization.title'),
      description: t('kiosk.features.customization.description'),
      color: '#1565C0'
    },
    {
      icon: <Analytics />,
      title: t('kiosk.features.analytics.title'),
      description: t('kiosk.features.analytics.description'),
      color: '#795548'
    },
    {
      icon: <Language />,
      title: t('kiosk.features.multiLanguage.title'),
      description: t('kiosk.features.multiLanguage.description'),
      color: '#FF5722'
    }
  ];

  const stats = [
    { number: '500+', label: t('kiosk.stats.installations') },
    { number: '10K+', label: t('kiosk.stats.dailyOrders') },
    { number: '95%', label: t('kiosk.stats.satisfaction') },
    { number: '7/24', label: t('kiosk.stats.support') }
  ];

  const benefits = [
    t('kiosk.benefits.reducedWaiting'),
    t('kiosk.benefits.increasedEfficiency'),
    t('kiosk.benefits.betterAccuracy'),
    t('kiosk.benefits.customerSatisfaction'),
    t('kiosk.benefits.dataInsights'),
    t('kiosk.benefits.brandExperience')
  ];

  const faqs = [
    {
      question: t('kiosk.faq.q1.question'),
      answer: t('kiosk.faq.q1.answer')
    },
    {
      question: t('kiosk.faq.q2.question'),
      answer: t('kiosk.faq.q2.answer')
    },
    {
      question: t('kiosk.faq.q3.question'),
      answer: t('kiosk.faq.q3.answer')
    },
    {
      question: t('kiosk.faq.q4.question'),
      answer: t('kiosk.faq.q4.answer')
    }
  ];

  return (
    <PageContainer>
      <Helmet>
        <title>{t('kiosk.meta.title')}</title>
        <meta name="description" content={t('kiosk.meta.description')} />
        <meta name="keywords" content={t('kiosk.meta.keywords')} />
      </Helmet>
      
      {/* Scroll Progress Bar */}
      <ScrollProgressBar
        sx={{
          '&::after': {
            width: `${scrollProgress}%`,
          }
        }}
      />
      
      <Header />

      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg">
            <Grid container alignItems="center" spacing={4}>
              {/* Left Side - Content */}
              <Grid item xs={12} lg={8}>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 800,
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textAlign: { xs: 'center', lg: 'left' }
                  }}
                >
                  {t('kiosk.hero.title')}
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' },
                    fontWeight: 400,
                    mb: 4,
                    opacity: 0.9,
                    maxWidth: '700px',
                    mx: { xs: 'auto', lg: 0 },
                    whiteSpace: 'pre-line',
                    textAlign: { xs: 'center', lg: 'left' }
                  }}
                >
                  {t('kiosk.hero.subtitle')}
                </Typography>
                
                <Box sx={{ mb: 4, textAlign: { xs: 'center', lg: 'left' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      mr: 2,
                      mb: { xs: 2, sm: 0 },
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      backgroundColor: 'white',
                      color: BRAND.colors.primary,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => navigate('/contact')}
                  >
                    {t('kiosk.buttons.requestDemo')}
                  </Button>
                </Box>

                {/* Quick Navigation */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: { xs: 'center', lg: 'flex-start' }, 
                  gap: 2.5, 
                  flexWrap: 'wrap', 
                  mb: 4 
                }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                      fontWeight: 700,
                      px: 1.2,
                      py: 0.6,
                      '&:hover': { color: 'white' }
                    }}
                    onClick={() => scrollToSection('features')}
                  >
                    {t('kiosk.navigation.features')}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                      fontWeight: 700,
                      px: 1.2,
                      py: 0.6,
                      '&:hover': { color: 'white' }
                    }}
                    onClick={() => scrollToSection('benefits')}
                  >
                    {t('kiosk.navigation.benefits')}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                      fontWeight: 700,
                      px: 1.2,
                      py: 0.6,
                      '&:hover': { color: 'white' }
                    }}
                    onClick={() => scrollToSection('faq')}
                  >
                    {t('kiosk.navigation.faq')}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                      fontWeight: 700,
                      px: 1.2,
                      py: 0.6,
                      '&:hover': { color: 'white' }
                    }}
                    onClick={() => scrollToSection('cta')}
                  >
                    {t('kiosk.navigation.getStarted')}
                  </Button>
                </Box>
              </Grid>

              {/* Right Side - Kiosk Device Image */}
              <Grid item xs={12} lg={4} sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Box 
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    position: 'relative'
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/kiokspanel.png"
                    alt="Self-Service Kiosk Device"
                    onError={(e: any) => {
                      // Eğer görsel yüklenemezse, fallback placeholder göster
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '500px',
                      height: 'auto',
                      width: 'auto',
                      filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))',
                      transform: 'perspective(1000px) rotateY(-5deg)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'perspective(1000px) rotateY(0deg) translateY(-10px)',
                        filter: 'drop-shadow(0 30px 60px rgba(0, 0, 0, 0.4))',
                      }
                    }}
                  />
                  {/* Fallback Placeholder */}
                  <Box
                    sx={{
                      display: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 300,
                      height: 400,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '2px dashed rgba(255, 255, 255, 0.3)',
                      borderRadius: 2,
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Kiosk Device
                    </Typography>
                    <Typography variant="body2">
                      Görsel yükleniyor...
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Stats Section - Full Width */}
            <StatsSection sx={{ mt: { xs: 4, lg: 6 } }}>
              <Grid container spacing={4}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box textAlign="center">
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800, 
                          color: 'white',
                          fontSize: { xs: '1rem', md: '1.8rem' }
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.7rem', md: '0.8rem' }
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </StatsSection>
          </Container>
        </HeroSection>
  {/* Pricing Section */}
        <PricingSection 
          backgroundColor="#f8f9fa"
          titleColor={BRAND.colors.primary}
          source="kiosk"
          pricingPlans={localizedKioskPlans}
          title={t('kiosk.pricing.title')}
        />

        {/* Features Section */}
        <Box id="features" sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('kiosk.features.title')}
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <FeatureCard>
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                      <Avatar
                        sx={{
                          backgroundColor: feature.color,
                          width: 64,
                          height: 64,
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ fontWeight: 600, color: '#333' }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ color: '#666', lineHeight: 1.6 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Benefits Section */}
        <Box id="benefits" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('kiosk.benefits.title')}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 4, height: '100%' }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                    {t('kiosk.benefits.subtitle')}
                  </Typography>
                  <List>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={benefit}
                          primaryTypographyProps={{ fontSize: '0.9rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/gts_takip_web.png"
                    alt={t('kiosk.benefits.imageAlt')}
                    sx={{
                      width: '120%',
                      height: 'auto',
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      display: 'block',
                      mx: 'auto',
                      transform: 'scale(1.2)'
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Box id="faq" sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="md">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('kiosk.faq.title')}
            </Typography>
            {faqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>
        </Box>

      
        {/* CTA Section */}
        <Box id="cta" sx={{ py: 8, backgroundColor: BRAND.colors.primary, color: 'white' }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.5rem' }
              }}
            >
              {t('kiosk.cta.title')}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              {t('kiosk.cta.subtitle')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                backgroundColor: 'white',
                color: BRAND.colors.primary,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => navigate('/contact')}
            >
              {t('kiosk.cta.button')}
            </Button>
          </Container>
        </Box>
        <Footer />
      </ContentWrapper>

      {/* WhatsApp Floating Button */}
      <PublicAiChatWidget />
    </PageContainer>
  );
};

export default Kiosk;
